import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { fromEvent, of, Subscription, forkJoin, switchMap, catchError } from 'rxjs';
import { GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgFor, NgIf } from '@angular/common';

import { padSupplierOrderReference } from '../../../shared/utils/padding';
import { User } from '../../../shared/models/user';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { truncateTwoDecimals } from '../../../shared/utils/number.format';
import { Batch, BatchShipment, ShipmentTracking } from '../../models/batch';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { BatchesService } from '../../services/batches.service';
import { CommonGridOptions, clientSideAgGridModules } from '../../../shared/datacenter/grid-options';
import { UserService } from '../../../shared/services/user.service';
import { CommonColDef, DateColDef, IconRedirectColDef, LinkColDef } from '../../../shared/datacenter/column-defs';
import { TranslationService } from '../../../shared/services/translation.service';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import {
	Document,
	GlobalDocument,
	DOCUMENTS_SUB_ENTITY,
	FieldOfDocument
} from '../../../commercial-documents/models/document';
import { AllQualityIssue } from '../../../claim/models/all-quality-issue';
import { ClaimBatchesService } from '../../../claim/services/batches.service';
import { ClaimBatch } from '../../../claim/models/claim-batch';
import { AllQualityIssueService } from '../../../claim/services/all-quality-issue.service';
import { DialogBatchNotInvoicedComponent } from '../../../claim/components/utils/dialog-batch-not-invoiced.component';
import { ECallOffService } from '../../../ecall-off/services/ecall-off.service';
import { DatacenterFilterType } from '../../../shared/models/datacenter-filters';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { UserDatacenterView } from '../../../shared/models/datacenter-aggrid-view';
import { ModelInfoPipe } from '../../../shared/pipes/model-info.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';
const DOCUMENT_TYPE_ORDER_ACKNOWLEDEGEMENT = 'OA';

const resizeObservable = fromEvent(globalThis, 'resize');

interface ShipmentData extends BatchShipment {
	step: string;
}

const linkToDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<Document, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const document = params.data;
		let documentType;
		switch (document.type) {
		case 'IN':
			documentType = 'invoice-card';
			break;
		case 'CE':
			documentType = 'certificate-card';
			break;
		case 'DA':
			documentType = 'dispatch-advice-card';
			break;
		case 'OA':
			documentType = 'order-acknowledgment-card';
			break;
		case 'XCARB':
			documentType = 'xcarb-document-card';
			break;
		default:
			throw new Error('unknown document type');
		}

		return {
			commands: [
				'/commercial-documents',
				'cards',
				documentType,
				document.id
			],
		};
	}
};

@Component({
	selector: 'copo-batch-card',
	templateUrl: './batch-card.component.html',
	styleUrl: './batch-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,

		MatButtonModule,
		MatIconModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		RouterLink,
		AgGridModule,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		ModelInfoPipe,
		NgFor,
		NgClass
	]
})
export class BatchCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	isLoading = false;
	isOffline = false;
	batch?: Batch;
	batchId: string;

	invoiceDisplayed = false;
	certificateDisplayed = false;
	dispatchAdviceDisplayed = false;
	orderAcknowledegeDisplayed = false;
	isXcarbDisplayed = false;

	shipmentData?: ShipmentData[];
	shipmentTracking: ShipmentTracking[] = [];

	documentResults: PaginatedResult<Document> | undefined;

	gridCommercialDocumentsOptions?: CommonGridOptions<GlobalDocument>;
	overlayNoRowsTemplate?: string;
	overlayNoDocumentsTemplate?: string;
	batchSub: Subscription;
	resizeSub?: Subscription;

	globalDocuments: GlobalDocument[] = [];

	documentsFields: readonly FieldOfDocument[] = [
		'id',
		'subtype',
		'type',
		'date',
		'name',
		'millCode',
		'proforma',
		`${ DOCUMENTS_SUB_ENTITY }.vendorBatchId`,
		`${ DOCUMENTS_SUB_ENTITY }.millCode`
	];

	readonly agGridModules = clientSideAgGridModules;
	hasClaimService: boolean;
	claimBatch: ClaimBatch | undefined;
	hasBatchDetailsError = false;
	hasCommercialDocumentError = false;
	private readonly claimBatchSub: Subscription;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly batchService: BatchesService,
		private readonly claimBatchService: ClaimBatchesService,
		private readonly commercialDocumentService: DocumentService,
		private readonly translationService: TranslationService,
		private readonly claimService: AllQualityIssueService,
		private readonly ecallOffService: ECallOffService,

		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly defaultConf: DatacenterDefaultConfiguration
	) {
		this.batchId = <string> this.route.snapshot.params['id'];
		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.user = this.userService.user;
		this.hasClaimService = this.user.hasService('QUAPP');
		this.invoiceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.certificateDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.dispatchAdviceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
		this.orderAcknowledegeDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.isXcarbDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'XCarb', 'True');
		this.overlayNoRowsTemplate = this.translationService.getTR('order-card.no-rows-to-show');
		this.overlayNoDocumentsTemplate = this.translationService.getTR('order.no-documents');
		this.batchSub = this.batchService.getBatchCard(this.batchId)
			.pipe(switchMap(batch => this.createBatchAndDocumentObservable(batch)))
			.subscribe({
				next: ([
					batch,
					documentResults,
				]) => {
					if (Object.keys(batch).length === 0) {
						this.isLoading = false;
						this.hasBatchDetailsError = true;
					}
					this.batch = batch;
					this.documentResults = documentResults;
					this.shipmentData = this.constructShipmentsData(batch);
					this.constructCommercialDocuments();
					this.constructGridCommercialDocumentsOptions(this.globalDocuments);

					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
					this.hasBatchDetailsError = true;
				}
			});

		this.claimBatchSub = this.userService.getSoldToRelationshipGroupsTransac('QUAPP', false)
			.pipe(
				switchMap(transacGrps => {
					if (transacGrps.length === 0) {
						return of(undefined);
					}
					const soldTos = transacGrps.map(soldtoList => soldtoList.soldTo);
					const shipTos = Array.from(new Set(transacGrps.flatMap(v => v.shipTos)));
					if (soldTos.length === 0 && shipTos.length === 0) {
						return of(undefined);
					}
					return this.claimBatchService.getBatchData(soldTos, shipTos, this.batchId);
				})
			)
			.subscribe({
				next: batchDataSoldToTransacs => {
					const batchData = batchDataSoldToTransacs ? batchDataSoldToTransacs.batchData : undefined;
					if (batchData) {
						this.claimBatch = batchData;
					}
				},
				error: (err: unknown) => {
					console.error('error getting batch data', err);
				}
			});
	}

	get producedLengthInMeters(): number | null {
		if (!this.batch || this.batch.form !== '04' || !this.batch.producedLength) {
			return null;
		}
		return truncateTwoDecimals(Number(this.batch.producedLength) / 1000);
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		this.batchSub.unsubscribe();
		this.claimBatchSub.unsubscribe();
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	async setMainBatchOnClaim() {
		const claim = this.claimService.claim = new AllQualityIssue();
		const { claimBatch } = this;
		if (!claimBatch) {
			return;
		}
		if (!this.claimService.batchInvoicedOrNot(claimBatch.batchInvoiced)) {
			const batchReferenceArray: string[] = [];
			batchReferenceArray.push(claimBatch.batchReference);
			this.dialog.open<DialogBatchNotInvoicedComponent>(
				DialogBatchNotInvoicedComponent,
				{ data: batchReferenceArray,
					panelClass: 'copo-claim-dialog',
					disableClose: true }
			);
			return;
		}
		this.claimService.fillClaimFromMainBatch(claim, claimBatch);
		await this.router.navigate(['../../../../quality-issues/new/quality-issue-entry']);
	}

	async createCallOff() {
		if (!this.batch) {
			return;
		}
		// Pre-select this batch in the ecall-off service
		this.ecallOffService.setSelectedBatches([this.batch]);
		this.ecallOffService.markBatchCardRedirect();
		// Navigate to Material Picking step of ecall-off workflow
		await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking']);
	}

	async close(defaultView: UserDatacenterView) {
		await this.router.navigate(['/commercial-documents/datacenter'], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async seeAllDocuments() {
		if (!this.batch) {
			return;
		}
		const defaultView = this.defaultConf.getDefaultView('document');
		const filtersList: DatacenterFilterType[] = [];
		if (this.orderAcknowledegeDisplayed) {
			const supplierOrderNumberFilter: DatacenterFilterType = {
				id: 'supplierOrderNumber',
				values: [
					{
						reference: this.batch.supplierOrderNumber,
						item: this.batch.supplierOrderItemNumber,
						entityReferenceName: 'documentLines'
					},
					{
						reference: !this.batch.supplierOrderNumber.startsWith('0')
							? `0${ this.batch.supplierOrderNumber }` : this.batch.supplierOrderNumber,
						item: this.batch.supplierOrderItemNumber,
						entityReferenceName: 'documentLines'
					}
				]
			};
			filtersList.push(supplierOrderNumberFilter);
		} else {
			const batchNumberFilter: DatacenterFilterType = {
				id: 'batchNumber',
				values: [this.batch.batchReference]
			};
			filtersList.push(batchNumberFilter);
		}
		defaultView.filterState = filtersList;
		await this.close(defaultView);
	}

	getTransportModeIcon(transportMode: string) {
		const modeMappings: Record<string, string> = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			2: 'train',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'03': 'train',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			6: 'train',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			7: 'train',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			3: 'in_transit',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			1: 'on_vessel',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			5: 'on_vessel',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			4: 'air-plane',
		};
		return modeMappings[transportMode] || '';
	}

	isLastIndexObject(index: number) {
		const length = this.shipmentTracking.length - 1;
		return index !== length;
	}

	getStockAlertIcon(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		return this.batchService.getStockAlertIcon(stockAlert);
	}

	getStockAlertTranslation(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		return this.batchService.getStockAlertTranslation(stockAlert);
	}

	private createBatchAndDocumentObservable(batch: Batch) {
		return forkJoin(
			[
				of(batch),
				(this.invoiceDisplayed || this.certificateDisplayed || this.dispatchAdviceDisplayed
					|| this.orderAcknowledegeDisplayed || this.isXcarbDisplayed) && Object.keys(batch).length > 0
					? this.commercialDocumentService.getDocuments(
						this.documentsFields,
						this.constructDocumentFilter(
							batch.supplierOrderNumber,
							batch.supplierOrderItemNumber,
							batch.batchReference,
							batch.supplierOrderType
						),
						0,
						10,
						new OrderBy(SORT_BY, SORT_WAY),
						false
					).pipe(
						catchError((err: unknown) => {
							console.error(err);
							this.hasCommercialDocumentError = true;
							return of(undefined);
						})
					)
					: of(undefined)
			]
		);
	}

	private constructDocumentFilter(
		supplierOrderReference: string,
		supplierOrderReferenceItem: string,
		batchReference: string,
		supplierOrderType: string
	) {
		const supplierOrderReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReferenceItem`,
			term: supplierOrderReferenceItem,
			quoted: true
		};
		const batchNumberField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.batchNumber`,
			term: batchReference,
			quoted: true
		};

		const supplierOrderReferenceFieldFromDoc: FieldExpression = {
			field: 'supplierOrderReference',
			term: padSupplierOrderReference(supplierOrderReference),
			quoted: true
		};
		const typeFieldFromDoc: FieldExpression = {
			field: 'type',
			term: DOCUMENT_TYPE_ORDER_ACKNOWLEDEGEMENT,
			quoted: true
		};
		const supplierOrderReferenceFieldFromDocLine: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReference`,
			term: padSupplierOrderReference(supplierOrderReference),
			quoted: true
		};
		const supplierOrderReferenceField: NodeExpression = {
			left: supplierOrderReferenceFieldFromDoc,
			operator: 'OR',
			right: supplierOrderReferenceFieldFromDocLine,
			parenthesized: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceField,
			operator: 'AND',
			right: supplierOrderReferenceItemField,
			parenthesized: true
		};
		const batchAndTypeField: NodeExpression = {
			left: typeFieldFromDoc,
			operator: 'OR',
			right: batchNumberField,
			parenthesized: true
		};
		const batchNumberNode = {
			left: supplierOrderType === 'C' ? supplierOrderNumberNode : supplierOrderReferenceField,
			operator: 'AND',
			right: this.orderAcknowledegeDisplayed ? batchAndTypeField : batchNumberField,
			parenthesized: true
		};
		return luceneToString(batchNumberNode);
	}

	private constructCommercialDocuments() {
		if (!this.documentResults) {
			return;
		}
		for (const document of this.documentResults.results) {
			const documentLine: GlobalDocument = {
				title: document.type === 'IN' ? this.translationService.getTR('order.card.document.invoices')
					: document.type === 'DA' ? this.translationService.getTR('order.card.document.dispatch.advices')
						: document.type === 'CE' ? this.translationService.getTR('order.card.document.certificates')
							: document.type === 'XCARB'
								? this.translationService.getTR('order.card.xcarb')
								: this.translationService.getTR('order.card.document.order.acknowledgments'),
				name: document.name,
				date: document.date,
				id: document.id,
				subtype: document.subtypeDescription,
				type: document.type,
				proforma: document.proforma
			};

			this.globalDocuments.push(documentLine);
		}
	}

	private constructGridCommercialDocumentsOptions(globalDocuments: GlobalDocument[]) {
		this.gridCommercialDocumentsOptions = new CommonGridOptions<GlobalDocument>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: globalDocuments,

			columnDefs: [
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.document'),
					field: 'title',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.type'),
					field: 'subtype',
				}),
				...globalDocuments.some(d => d.proforma === 'true') ? [
					new CommonColDef<GlobalDocument>({
						headerName: this.translationService.getTR('order-card.proforma'),
						field: 'proforma',
						sortable: true,
						valueFormatter: params => {
							if (params.value === 'true') {
								return this.translationService.getTR('global.yes');
							}
							if (params.value === 'false') {
								return this.translationService.getTR('global.no');
							}
							return '';
						}
					})
				] : [],
				new LinkColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.name'),
					field: 'name',
					cellRendererParams: linkToDocumentCellRendererParams
				}),
				new DateColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order.creation.date'),
					field: 'date',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<GlobalDocument>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<Document, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							return {
								url: this.commercialDocumentService
									.getDownloadDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}

	private constructShipmentsData(batch: Batch) {
		const fullShipments: ShipmentData[] = [];
		if (batch.shipments !== undefined) {
			batch.shipments.sort((ship1, ship2) =>
				moment(ship1.dispatchAdviceDate).diff(moment(ship2.dispatchAdviceDate)));
			for (const [index, item] of batch.shipments.entries()) {
				const shipment: ShipmentData = {
					step: this.generateStep(index, batch.shipments.length),
					...item
				};
				fullShipments.push(shipment);
				const isFirstValue = batch.shipments.indexOf(item) === 0;
				const tracking: ShipmentTracking = {
					shipmentReferenceDate: item.lineShipmentReferenceDate,
					location: isFirstValue ? batch.millDescription
						: batch.shipments[batch.shipments.indexOf(item) - 1].shipmentDestinationDescription,
					transportMode: item.shipmentTransportMode,
					transportModeDescription: item.shipmentTransportModeDescription,
					transportReference: item.shipmentTransportReference,
					showIcon: isFirstValue,
					dispatchAdviceNumber: item.dispatchAdviceNumber
				};
				this.shipmentTracking.push(tracking);
				if (index === batch.shipments.length - 1) {
					const finalTracking: ShipmentTracking = {
						location: batch.shipments[batch.shipments.indexOf(item)].shipmentDestinationDescription,
						showIcon: isFirstValue,
						transportMode: batch.shipments[batch.shipments.indexOf(item)].shipmentTransportMode,
					};
					this.shipmentTracking.push(finalTracking);
				}
			}
		}
		return fullShipments;
	}

	private generateStep(currentIndex: number, arrayLength: number) {
		let step = this.translationService.getTR('shipment.last');

		if (currentIndex < arrayLength - 1) {
			step = `${ this.translationService.getTR('shipment.last') }-${ (arrayLength - currentIndex) - 1 }`;
		}
		if (currentIndex === 0 && arrayLength > 1) {
			step = this.translationService.getTR('shipment.first');
		}
		if (currentIndex === arrayLength) {
			step = this.translationService.getTR('shipment.last');
		}
		return step;
	}
}
